'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { clearClientSessionState, hasAuthTokenCookie } from '@/lib/auth/clearClientSessionState';
import { loginRouteForRoles } from '@/lib/auth/loginRoute';
import { subscribeToSignOut } from '@/lib/auth/sessionBroadcast';
import { useAppUser } from '@/lib/context/AppUserContext';
import { createSPAClient } from '@/lib/supabase/client';
import { trackingManager } from '@/lib/tracking/tracking-manager-instance';
import { redirectToLogin, reloadPage } from '@/lib/url';

/**
 * Drops the previous user's client state whenever the browser's Supabase session stops
 * belonging to the user whose data is sitting in it, and follows a sign-out that
 * happened in another tab.
 *
 * Must be rendered inside QueryClientProvider, which is why this is a component
 * rather than a hook: TanstackQueryClientProvider sits outside its own provider and
 * cannot call useQueryClient() itself.
 *
 * This is a safety net, NOT the primary sign-out path — that is `useSignOut`. It
 * cannot see a sign-out performed by the `logout()` server action: that clears the
 * auth cookies with a Set-Cookie header, and nothing pushes an event when cookies
 * change underneath the browser client. Verified against @supabase/auth-js 2.x, whose
 * storage-recovery and token-refresh paths both return early on a missing session
 * without emitting, while BroadcastChannel only relays events another JS client
 * emitted. That is why the deliberate sign-out must tear down explicitly.
 *
 * What it does catch: another tab whose refresh token was revoked by the global-scope
 * signOut (its next auto-refresh tick fails non-retryably and emits SIGNED_OUT),
 * expired or server-revoked sessions, client-initiated sign-outs, and a sign-in as a
 * different user inside a document that survived.
 */
/** Whether this document is showing an authenticated route. */
function isInsideApp(): boolean {
  if (typeof window === 'undefined') return false;
  const { pathname } = window.location;
  return pathname === '/app' || pathname.startsWith('/app/');
}

export function AuthCacheSync() {
  const queryClient = useQueryClient();
  // Server-rendered for this document, so it still names the user the page was built
  // for once the session is gone: whose login page a signed-out document should land on.
  const { user } = useAppUser();
  const rolesRef = useRef<string[]>([]);
  rolesRef.current = user?.role ?? [];
  // The identity this document's rows belong to. Seeded from the server render, so the
  // first auth event is compared against the user the page was actually built for: if
  // another tab switched users between that render and this subscription, both one-shot
  // broadcasts were missed and the first event already carries the new user. `undefined`
  // (no server-rendered user) means "adopt whatever arrives first" rather than clearing
  // the cache React Query just hydrated.
  const lastUserIdRef = useRef<string | null | undefined>(user?.auth_user_id ?? undefined);

  useEffect(() => {
    // createBrowserClient is a browser singleton, so this hands back the same GoTrue
    // instance every other call site holds. Building it in the effect rather than
    // during render also keeps document.cookie out of the render path.
    const supabase = createSPAClient();

    // A signed-out document with nothing left to correct it: the session ended while it
    // was loading, or while it sat in the background or the back-forward cache, and the
    // one-shot broadcast never reached it. GoTrue's recovery returns early when it finds
    // no session and emits nothing, so this is the only catch. clear() does not touch
    // what is already rendered, and AppUserContext still names that user, so the document
    // has to go. With the cookie still present (GoTrue also reports null after a refresh
    // that failed on a flaky network) nothing happens: the proxy settles it next request.
    const leaveIfSessionCookieGone = () => {
      if (!isInsideApp() || hasAuthTokenCookie()) return;
      lastUserIdRef.current = null;
      clearClientSessionState(queryClient);
      redirectToLogin(loginRouteForRoles(rolesRef.current));
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      const nextUserId = session?.user?.id ?? null;

      // Emitted to each subscriber the moment it subscribes, carrying whatever session
      // already exists. Under StrictMode the effect subscribes twice, so this runs
      // twice — still a no-op. No session: decided by the cookie check above. A session
      // for someone other than the server-rendered user falls through to the identity
      // change below, exactly like a later SIGNED_IN would.
      if (event === 'INITIAL_SESSION' && nextUserId === null) {
        // Leaving forgets the identity; staying (cookie still present) keeps the
        // server-rendered one, so the same user's later recovery is not a user change.
        leaveIfSessionCookieGone();
        return;
      }

      if (event === 'SIGNED_OUT') {
        // The deliberate path (useSignOut) clears cookies server-side and never emits
        // here, so every SIGNED_OUT this net sees is a session the person did not end.
        // Tracked before the teardown below resets the analytics identity.
        trackingManager.trackEvent('AUTH_SESSION_LOST', { event });
        lastUserIdRef.current = null;
        // Full teardown, matching useSignOut. A session that ends without going
        // through the sign-out button (revoked elsewhere, expired, another tab)
        // must not leave scanned-employee PII behind on a shared terminal.
        clearClientSessionState(queryClient);
        // Clearing empties the cache but leaves mounted observers rendering their last
        // snapshot, so a tab sitting in /app would keep the previous rows on screen.
        // Only replacing the document removes them, exactly as useSignOut does, and on
        // the login page that matches the user this document was rendered for.
        if (isInsideApp()) {
          redirectToLogin(loginRouteForRoles(rolesRef.current));
        }
        return;
      }

      // Everything else is decided by identity, not by event name. That matters
      // most for SIGNED_IN, which GoTrue re-emits on every tab focus for a user who
      // is already signed in: keying off the name would throw the cache away each
      // time someone alt-tabs back. TOKEN_REFRESHED and USER_UPDATED are the same.
      if (lastUserIdRef.current !== undefined && lastUserIdRef.current !== nextUserId) {
        // A different person now owns this document. Drop the previous user's data,
        // but leave the analytics identity alone: this fires while the login page is
        // identifying the new user, and resetting here could undo that.
        clearClientSessionState(queryClient, { resetTracking: false });
        // AppUserContext is server-rendered and still holds the previous user's name,
        // email and tenant, and the rendered rows are still theirs. Only a fresh
        // document re-runs AppUserServerProvider for whoever is signed in now.
        if (isInsideApp()) {
          reloadPage();
        }
      }
      lastUserIdRef.current = nextUserId;
    });

    // A sibling tab signing out reaches us here, immediately, rather than whenever this
    // tab's token refresh happens to fail.
    const unsubscribeFromSignOut = subscribeToSignOut((loginRoute) => {
      lastUserIdRef.current = null;
      clearClientSessionState(queryClient);

      // Only tabs sitting inside the app need moving. One already on a public or auth
      // page is where it should be, and yanking it mid-login would be hostile.
      if (isInsideApp()) {
        redirectToLogin(loginRoute);
      }
    });

    // Shown again without a request through the proxy: restored from the back-forward
    // cache, or a background tab brought back after the session ended elsewhere.
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) leaveIfSessionCookieGone();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') leaveIfSessionCookieGone();
    };
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      subscription.unsubscribe();
      unsubscribeFromSignOut();
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [queryClient]);

  return null;
}
